import { useEffect } from 'react';

import Decimal from 'decimal.js';
import { useFormContext } from 'react-hook-form';

import { parseToFloat } from '../utils/parseToFloat';
import { percentageToDecimalNumber } from '../utils/percentageToDecimal';
import { percentageFields } from '../EmployerCostSettings.constants';

const calculateTotal = (values: Record<string, string>): number => {
  return Object.values(values).reduce(
    (acc, cur) => Decimal.add(acc, parseToFloat(cur)).toNumber(),
    0
  );
};

/**
 * Calculates the sum of the values, based on the given list of the fields' names,
 * and updates the value of the `totalField` with the calculated value.
 * @param fieldList - the name of the fields what we want to sum
 * @param totalField -  the name of the field what we want to update with the calculated total value
 */
export const useCalculateTotal = (
  fieldList: string[],
  totalField: string
): void => {
  const { setValue, watch } = useFormContext();

  const watchedObjects = fieldList.reduce((acc, cur) => {
    const value = watch(cur);
    return {
      ...acc,
      [cur]: percentageFields.includes(cur)
        ? percentageToDecimalNumber(value)
        : value
    };
  }, {});

  const watchedTotal = watch(totalField);

  useEffect(() => {
    const calculatedTotal = calculateTotal(watchedObjects);

    if (watchedTotal !== calculatedTotal) {
      setValue(totalField, calculatedTotal);
    }
  }, [watchedObjects, watchedTotal]);
};
